import {
    INTERSTITIAL_ALERT_TYPE,
    type InterstitialAlertType,
    PRICE_CHANGE_VARIANT,
    type PriceChangeVariant
} from '@/components/srp/FareCards/InterstitialAlerts/constants'
import { formatCurrency } from '@/utils'

/** BE fetch-interstitial-page price-integrity dialogue. */
export type InterstitialUserDialogueMessage = {
    type?: string
    title?: string
    subTitle?: string
    originalPrice?: number
    currentPrice?: number
    difference?: number
}

export type InterstitialUserDialogue = {
    dialogueType?: string
    message?: InterstitialUserDialogueMessage
}

export type ParsedInterstitialPriceAlert = {
    type: InterstitialAlertType
    variant?: PriceChangeVariant
    title?: string
    subTitle?: string
    newPrice: string
    priceDelta: string
    originalPrice?: number
    currentPrice?: number
    difference?: number
}

const PRICE_DIALOGUE_TYPES = new Set(['PRICE_INCREASE', 'PRICE_DECREASE', 'FLIGHT_NOT_AVAILABLE'])

const formatRupee = (value?: number): string => {
    if (typeof value !== 'number' || Number.isNaN(value)) return ''
    return formatCurrency(value)
}

const formatSignedDelta = (difference?: number, dialogueType?: string): string => {
    if (typeof difference !== 'number' || Number.isNaN(difference)) return ''
    const abs = Math.abs(difference)
    const formatted = formatCurrency(abs)
    if (dialogueType === 'PRICE_DECREASE' || difference < 0) {
        return `(- ${formatted})`
    }
    return `(+ ${formatted})`
}

/**
 * Normalise fetch-interstitial-page response shapes into a single dialogue object.
 * Supports camelCase `userDialogue`, itinerary-style `USER_DIALOGUE.dialogues[]`,
 * and a lone dialogue object at the top level.
 */
export const extractInterstitialUserDialogue = (responseData: any): InterstitialUserDialogue | null => {
    if (!responseData || typeof responseData !== 'object') return null

    const direct = responseData.userDialogue || responseData.USER_DIALOGUE
    if (!direct) return null

    if (Array.isArray(direct.dialogues) && direct.dialogues.length > 0) {
        const first = direct.dialogues[0] || {}
        return {
            dialogueType: first.dialogueType,
            message: first.message
        }
    }

    if (direct.dialogueType || direct.message) {
        return {
            dialogueType: direct.dialogueType,
            message: direct.message
        }
    }

    return null
}

export const mapUserDialogueToInterstitialAlert = (
    dialogue: InterstitialUserDialogue | null | undefined
): ParsedInterstitialPriceAlert | null => {
    if (!dialogue?.dialogueType || !PRICE_DIALOGUE_TYPES.has(dialogue.dialogueType)) {
        return null
    }

    const { dialogueType, message } = dialogue
    // Default `message = {}` does not cover BE `message: null` — that threw on destructure.
    const { title, subTitle, originalPrice, currentPrice, difference } = message ?? {}

    if (dialogueType === 'FLIGHT_NOT_AVAILABLE') {
        return {
            type: INTERSTITIAL_ALERT_TYPE.FLIGHT_UNAVAILABLE,
            title,
            subTitle,
            newPrice: '',
            priceDelta: '',
            originalPrice,
            currentPrice,
            difference
        }
    }

    const isIncrease = dialogueType === 'PRICE_INCREASE'
    return {
        type: isIncrease ? INTERSTITIAL_ALERT_TYPE.PRICE_INCREASE : INTERSTITIAL_ALERT_TYPE.PRICE_DROP,
        variant: isIncrease ? PRICE_CHANGE_VARIANT.INCREASE : PRICE_CHANGE_VARIANT.DROP,
        title,
        subTitle,
        newPrice: formatRupee(currentPrice),
        priceDelta: formatSignedDelta(difference, dialogueType),
        originalPrice,
        currentPrice,
        difference
    }
}
